// @ts-strict-ignore
import Space from 'antd/lib/space';
import { useUserCanPerformAction } from '@/utils/hooks/useUserCanPerformAction';
import { BuildDistroBuild, BuildDistroBuildStatus, BuildStatus, IntegrationCapability } from '../../types';
import { AppComponentProps, AppRouteComponentMatchParams } from '../../typescriptHelpers';
import { InfoCircleOutlined, QrcodeOutlined, NotificationOutlined, DownloadOutlined } from '@ant-design/icons';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import Tooltip, { TooltipPlacement } from 'antd/lib/tooltip';
import React from 'react';
import Text from 'antd/lib/typography/Text';
import Modal from 'antd/lib/modal';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { selectAppPlatform, selectAuthCredentials, selectIsDemoInstance } from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { QRCode } from 'react-qrcode-logo';
import Dropdown from 'antd/lib/dropdown';
import { canPlatformInstallBinaries, getShouldDisableInstallationForBinaryType } from '../../helpers';
import { helperTextForArtifactType, isDownloadOnlyBuild } from '../../helpers/displayHelpers';
import { trackBuildDistroViewEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import * as API from '../../api';
import { globalMessage } from '../../utils/globalMessage';
import { useParams } from 'react-router-dom';
import { createBuildDistroBuildRoute } from '../../helpers/routeHelpers';
import { MenuProps } from 'antd/lib/menu';
import useHasIntegrationCapabilityInstalled from '../../utils/hooks/useHasIntegrationCapabilityInstalled';
import useHasIntegrationTypeForCapability from '../../utils/hooks/useIntegrationTypeForCapability';
import * as Log from '../../utils/logger';
import Spin from 'antd/lib/spin';
import { PLATFORM_AVAILABILITY_MESSAGE } from '@/constants';
import { MenuItemType } from 'antd/lib/menu/interface';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '../HOCs/PermissionedActionButton';
import qs from 'query-string';

interface BuildDistroInstallMenuProps extends AppComponentProps {
    buildDistroBuild: BuildDistroBuild;
    shouldShowInfoIcon: boolean;
    dropDownStyle?: React.CSSProperties;
    customTooltipPlacement?: TooltipPlacement;
    bucketId?: string;
    children: (isDisabled: boolean, isLoading: boolean) => React.ReactNode;
}

enum InstallOptions {
    SendToMyselfOnComms,
    DisplayQRCode,
    Download,
}

enum CtaType {
    ShareToComms = 'shareLinkToComms',
    QRCode = 'qrCode',
}

const EnableAuthedBuildDistroInstallLink = false;

const BuildDistroInstallMenu: React.FC<BuildDistroInstallMenuProps> = (props: BuildDistroInstallMenuProps) => {
    const { orgId, appId, buildDistroBuild, customTooltipPlacement, bucketId } = props;
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const dispatch = useDispatch();

    const [sendInstallLinkToCommsIsLoading, setSendInstallLinkToCommsIsLoading] = React.useState(false);
    const [authTokenFetchIsLoading, setAuthTokenFetchIsLoading] = React.useState(false);
    const [accessToken, setAccessToken] = React.useState(null);
    const [authedQrCodeLink, setAuthedQrCodeLink] = React.useState(null);
    const [qrCodeModalVisible, setQrCodeModalVisible] = React.useState(false);

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));
    const hasCommsIntegrationInstalled = useHasIntegrationCapabilityInstalled(
        appId,
        IntegrationCapability.Notifications,
    );
    const commsIntegrationType = useHasIntegrationTypeForCapability(appId, IntegrationCapability.Notifications);
    const isDemoOrg = useSelector((state: RootState) => selectIsDemoInstance(state, orgId));
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const hasBinaryBuild = !!buildDistroBuild.binaryBuild;
    const downloadOnly = isDownloadOnlyBuild(buildDistroBuild);
    const buildType = props.buildDistroBuild.binaryBuild?.buildType;
    const buildDetailsQrCodeLink = `${window.location.origin}${createBuildDistroBuildRoute(
        orgIdParam,
        appIdParam,
        buildDistroBuild.id,
        buildDistroBuild.bucketId,
    )}`;

    const [icon, alt] = iconAndAltForIntegrationType(commsIntegrationType);

    const userCanPerformAction = useUserCanPerformAction(
        RunwayUserAction.UABucketInstallBuilds,
        undefined,
        undefined,
        bucketId,
        undefined,
        appId,
    );
    const userCanDownload = useUserCanPerformAction(
        RunwayUserAction.UADownloadBuildArtifact,
        undefined,
        undefined,
        bucketId,
        undefined,
        appId,
    );

    const shouldDisableInstallationForBinaryType =
        buildType ? getShouldDisableInstallationForBinaryType(buildType) : !userCanPerformAction;
    const shouldDisableInstallationForBinaryBuild = hasBinaryBuild ? shouldDisableInstallationForBinaryType : true;
    const shouldDisableBasedOnPlatform =
        canPlatformInstallBinaries(appPlatform) ? shouldDisableInstallationForBinaryBuild : true;
    const isExpired = buildDistroBuild.status === BuildDistroBuildStatus.Expired;
    const cannotDownload = downloadOnly && !userCanDownload;
    const cannotInstall = !downloadOnly && (!userCanPerformAction || shouldDisableBasedOnPlatform);
    const isDisabled = isExpired || cannotDownload || cannotInstall;

    let installationHelperText;
    if (buildDistroBuild.status === BuildDistroBuildStatus.Expired) {
        installationHelperText = 'This build has expired.';
    } else if (downloadOnly) {
        installationHelperText =
            userCanDownload ? 'This build is available to download.' : DISALLOWED_ACTION_ERROR_MESSAGE;
    } else if (!userCanPerformAction) {
        installationHelperText = DISALLOWED_ACTION_ERROR_MESSAGE;
    } else if (canPlatformInstallBinaries(appPlatform)) {
        if (buildType) {
            installationHelperText = helperTextForArtifactType(
                buildType,
                props.buildDistroBuild.binaryBuild?.provisioningProfile?.name,
            );
        } else if (hasBinaryBuild) {
            installationHelperText =
                'We were unable to infer the profile and signing certificate type of this build. It may not be installable on your device.';
        } else {
            installationHelperText = 'No build artifact found.';
        }
    } else {
        installationHelperText = PLATFORM_AVAILABILITY_MESSAGE;
    }

    const defaultQrLink = !isDemoOrg ? buildDetailsQrCodeLink : '';

    const menuItems: MenuItemType[] =
        downloadOnly ?
            []
        :   [
                {
                    label: (
                        <Space align="center">
                            {icon ?
                                <img src={icon} alt={alt} style={{ width: 16 }} />
                            :   <NotificationOutlined style={{ color: antGrey.grey5 }} />}
                            <Text
                                style={{
                                    color: hasCommsIntegrationInstalled ? runwayColors.darkTextGrey : antGrey.grey6,
                                }}
                            >
                                Share link with myself
                                {commsIntegrationType && ` on ${displayNameForIntegrationType(commsIntegrationType)}`}
                            </Text>
                            <Tooltip
                                title={
                                    hasCommsIntegrationInstalled ?
                                        `A link to the installation page for this build will be DM'd to you on ${displayNameForIntegrationType(
                                            commsIntegrationType,
                                        )}`
                                    :   `A link to the installation page for this build will be DM'd to you. Connect a notifications integration to enable this option`
                                }
                            >
                                <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                            </Tooltip>
                        </Space>
                    ),
                    key: InstallOptions.SendToMyselfOnComms,
                    disabled: !hasCommsIntegrationInstalled,
                },
            ];

    const authedBuildDistroDownloadLink = React.useMemo(() => {
        if (!accessToken || !buildDistroBuild) {
            return null;
        }
        const ciBuildInProgress = buildDistroBuild.ciBuild?.buildStatus === BuildStatus.InProgress;
        if (
            !ciBuildInProgress &&
            (buildDistroBuild.binaryBuild?.artifactFileName || downloadOnly) &&
            userCanDownload &&
            buildDistroBuild.downloadUrl
        ) {
            const params = {
                t: accessToken,
            };
            const authedUrl = qs.stringifyUrl({ url: buildDistroBuild.downloadUrl, query: params });

            return authedUrl;
        }

        return null;
    }, [buildDistroBuild.id, accessToken, userCanDownload, downloadOnly]);

    if (authedBuildDistroDownloadLink && (accessToken || authTokenFetchIsLoading)) {
        menuItems.push({
            label: (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'space-between',
                        gap: 8,
                        paddingTop: 8,
                    }}
                >
                    <Space align="center">
                        <DownloadOutlined style={{ color: runwayColors.primary }} />
                        <Text>
                            Download{' '}
                            <span style={{ color: runwayColors.primary }}>
                                {authTokenFetchIsLoading ?
                                    <Spin style={{ paddingLeft: '8px' }} size="small" />
                                :   (buildDistroBuild.binaryBuild?.artifactFileName ??
                                    buildDistroBuild.originalFileName)
                                }
                            </span>
                        </Text>
                    </Space>
                </div>
            ),
            key: InstallOptions.Download,
        });
    }

    if (!downloadOnly) {
        menuItems.push({
            label: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, paddingTop: 8 }}>
                    <Space align="center">
                        <QrcodeOutlined style={{ color: runwayColors.primary }} />
                        <Text>Install from QR Code</Text>
                    </Space>
                    {authTokenFetchIsLoading ?
                        <Spin>
                            <div style={{ height: 280, width: 280 }}></div>
                        </Spin>
                    :   <QRCode value={authedQrCodeLink ?? defaultQrLink} size={280} />}
                </div>
            ),
            key: InstallOptions.DisplayQRCode,
        });
    }

    const onClickMenuItem = (item) => {
        item.domEvent.stopPropagation();

        switch (parseInt(item.key)) {
            case InstallOptions.SendToMyselfOnComms: {
                setSendInstallLinkToCommsIsLoading(true);
                API.sendInstallLinkToMyselfOnComms(
                    {
                        orgId,
                        appId,
                        buildId: props.buildDistroBuild.id,
                        bucketId: props.buildDistroBuild.bucketId,
                    },
                    authCredentials,
                )
                    .then(() => {
                        globalMessage.success('Install link sent!');
                    })
                    .catch((error) => {
                        globalMessage.error(error.message);
                    })
                    .finally(() => {
                        trackCtaClickedEvent(CtaType.ShareToComms);
                        setSendInstallLinkToCommsIsLoading(false);
                    });
                break;
            }
            case InstallOptions.DisplayQRCode: {
                setQrCodeModalVisible(true);
                break;
            }
            case InstallOptions.Download: {
                if (authedBuildDistroDownloadLink) {
                    window.open(authedBuildDistroDownloadLink, '_self');
                }
                break;
            }
        }
    };

    const fetchAccessToken = React.useCallback(() => {
        setAuthTokenFetchIsLoading(true);
        API.getShortLivedToken(authCredentials)
            .then((accessToken) => {
                setAccessToken(accessToken);
            })
            .catch((e) => {
                Log.error(e);
            })
            .finally(() => {
                setAuthTokenFetchIsLoading(false);
            });
    }, [authCredentials]);

    if (EnableAuthedBuildDistroInstallLink) {
        React.useEffect(() => {
            const buildDetailsQrCodeLinkWithToken = `${buildDetailsQrCodeLink}&accessToken=${accessToken}`;
            setAuthedQrCodeLink(buildDetailsQrCodeLinkWithToken);
        }, [accessToken]);
    }

    const onOpenChange = (open: boolean) => {
        if (open && !isDemoOrg) {
            setAuthedQrCodeLink(null);
            trackCtaClickedEvent(CtaType.QRCode);
            fetchAccessToken();
        }
    };

    const trackCtaClickedEvent = (ctaType: CtaType) => {
        dispatch(
            trackBuildDistroViewEvent({
                orgId,
                appId,
                bucketId: props.buildDistroBuild?.bucketId,
                eventName: AnalyticEvents.Event.BuildDistroClickBuildInstallCta,
                extraProperties: {
                    ctaType,
                },
            }),
        );
    };

    const menuProps: MenuProps = {
        items: menuItems,
        onClick: onClickMenuItem,
    };

    const placement = customTooltipPlacement ?? 'top';

    const tooltipWrapper = (children) => {
        if (isDisabled) {
            return (
                <Tooltip title={installationHelperText} placement={placement}>
                    <div
                        style={{
                            width: '100%',
                        }}
                    >
                        <div
                            style={{
                                width: '100%',
                                pointerEvents: 'none',
                            }}
                        >
                            {children}
                        </div>
                    </div>
                </Tooltip>
            );
        }

        return children;
    };

    return (
        <>
            <Modal
                title={`Scan from your mobile device to install build #${buildDistroBuild.binaryBuild?.buildNumber}`}
                open={qrCodeModalVisible}
                onCancel={() => setQrCodeModalVisible(false)}
                destroyOnClose
                footer={null}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: 24,
                    }}
                >
                    {authTokenFetchIsLoading ?
                        <Spin>
                            <div style={{ height: 280, width: 280 }}></div>
                        </Spin>
                    :   <QRCode value={authedQrCodeLink ?? defaultQrLink} size={420} />}

                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                            gap: 16,
                        }}
                    >
                        <Text style={{ textAlign: 'center' }}>{installationHelperText}</Text>
                        <Text type="secondary" style={{ textAlign: 'center' }}>
                            This link is for your user only. Do not share it with anyone.
                        </Text>
                    </div>
                </div>
            </Modal>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...props.dropDownStyle }}>
                {tooltipWrapper(
                    <Dropdown
                        menu={menuProps}
                        trigger={['hover']}
                        disabled={isDisabled}
                        onOpenChange={onOpenChange}
                        destroyPopupOnHide
                    >
                        {props.children(isDisabled, sendInstallLinkToCommsIsLoading)}
                    </Dropdown>,
                )}
                {!isDisabled && props.shouldShowInfoIcon && installationHelperText && (
                    <Tooltip title={installationHelperText} placement={placement}>
                        <InfoCircleOutlined
                            style={{
                                color: runwayColors.info,
                            }}
                        />
                    </Tooltip>
                )}
            </div>
        </>
    );
};

export default BuildDistroInstallMenu;
